import * as THREE from 'three';
import { FikoSahne } from './FikoSahne';
import { FikoIsikYonselComponent } from './fiko-isiklar/fiko-isik-yonsel/fiko-isik-yonsel.component';
import { FikoWebGPULighting, fikoWebGPULighting } from './FikoWebGPULighting';

export class FikoIsikYonsel extends THREE.DirectionalLight {
    override type: "FikoIsikYonsel" | any;

    // 🚀 PERFORMANS: Pre-allocated vectors for updateLightPosition (zero allocation per frame)
    private static _offset = new THREE.Vector3();
    private static _tempPos = new THREE.Vector3();

    // 🚀 WebGPU Lighting entegrasyonu
    private static webGPULighting: FikoWebGPULighting | null = null;
    private static webGPUInitialized: boolean = false;
    private static webGPUInitPromise: Promise<void> | null = null;

    // Kamera takip modu
    kameraTakipAktif: boolean = false;
    kameraOffsetUzakligi: number = 20; // Kameradan ne kadar uzakta olacağı
    kameraManuelOffset: THREE.Vector3 = new THREE.Vector3(0, 0, 0); // Manuel offset (X,Y,Z)

    sagButonlar: any[] = [
    ]

    sagMenu: any[] = [{
        simge: null,
        baslik: "Gizle / Göster",
        tiklandi: () => {
            this.visible = !this.visible;
            this.sahne.anaGosterim.FikoYT.degisti.emit();
            this.sahne.anaGosterim.ciz();
        }
    },
    {
        simge: null,
        baslik: "Düzenle",
        tiklandi: () => {
            this.sahne.anaGosterim.DialogFiko.ac({
                bilesen: FikoIsikYonselComponent,
                parametreler: { nesne: this },
                $event: null,
                ayarlar: { baslik: 'Fiko Yönsel Işık', simgeResim: './simgeler/kurdela.webp' }
            });
        }
    },
    {
        simge: "trash alternate",
        baslik: "Sil",
        renkSinif: "red",
        tiklandi: () => {
            this.dispose();  // ✅ Dispose metodu çağrısı - memory leak önleme
            this.sahne.anaGosterim.FikoYT.degisti.emit();
            this.sahne.anaGosterim.ciz();
        }
    },
    ]

    aracCubugu: any[] = [
    ]

    constructor(public sahne?: FikoSahne, name?: string) {
        super();
        this.name = name;

        this.intensity = 1;
        this.color = new THREE.Color(0xffffffff);

        this.position.set(10, 10, 10);  // ✅ Tepeden sabit ışık pozisyonu

        // Başlangıçta manuel offset pozisyonu ile senkronize et
        this.kameraManuelOffset.copy(this.position);

        // Gölge desteği (CAD görünümü için)
        this.castShadow = true;
        this.shadow.mapSize.width = 2048;
        this.shadow.mapSize.height = 2048;
        this.shadow.camera.left = -100;
        this.shadow.camera.right = 100;
        this.shadow.camera.top = 100;
        this.shadow.camera.bottom = -100;
        this.shadow.camera.near = 0.5;
        this.shadow.camera.far = 500;

        this.userData['FikoArayuz'] = {};

        this.userData['FikoArayuz'].agactaGorunur = true;
        this.userData['FikoArayuz'].listeGoster = true;
        this.userData['FikoArayuz'].simge = "./simgeler/kurdela.webp";
    }

    ciftTiklandi() {
        this.sahne.anaGosterim.DialogFiko.ac({
            bilesen: FikoIsikYonselComponent,
            parametreler: { nesne: this },
            $event: null,
            ayarlar: { baslik: 'Fiko Yönsel Işık', simgeResim: './simgeler/kurdela.webp' }
        });
    }

    /**
     * ✅ Headlight Mode: Işığı kamerayla senkronize et
     * Kameranın önünden ışık gelir (FreeCAD/CATIA benzeri)
     * 🚀 PERFORMANS: Zero allocation - static vectors kullanılıyor
     */
    updateLightPosition(): void {
        if (!this.kameraTakipAktif || !this.sahne?.anaGosterim?.aktifKamera) {
            return;
        }

        const camera = this.sahne.anaGosterim.aktifKamera;

        // 🚀 PERFORMANS: Reuse static vectors instead of creating new ones
        const offset = FikoIsikYonsel._offset;
        const tempPos = FikoIsikYonsel._tempPos;

        // Offset: kamera yönünde kameraOffsetUzakligi kadar geri
        offset.set(0, 0, this.kameraOffsetUzakligi);
        offset.applyQuaternion(camera.quaternion);

        // Son pozisyon = kamera pozisyonu + offset + manuel offset
        tempPos.copy(camera.position).add(offset).add(this.kameraManuelOffset);

        this.position.copy(tempPos);
        this.target.position.copy(camera.position);
        this.target.updateMatrixWorld();
        this.updateMatrixWorld();
    }

    /**
     * ✅ Memory Leak Önleme: Shadow map ve resources temizleme
     * Işık silinirken tüm GPU resources serbest bırakılır
     */
    dispose(): void {
        // Shadow map'i temizle (~16MB texture)
        if (this.shadow && this.shadow.map) {
            this.shadow.map.dispose();
            this.shadow.map = null;
        }

        // Shadow camera'yı temizle
        if (this.shadow && this.shadow.camera) {
            this.shadow.camera = null;
        }

        // Parent'tan kaldır
        if (this.parent) {
            this.parent.remove(this);
        }

        // Target'ı temizle (DirectionalLight target özel handling)
        if (this.target && this.target.parent) {
            this.target.parent.remove(this.target);
        }
    }

    // ========================================================================
    // 🚀 WebGPU LIGHTING STATIC METHODS
    // ========================================================================

    /**
     * 🚀 WebGPU Lighting sistemini başlat (singleton)
     * İlk ışık oluşturulduğunda otomatik çağrılır
     */
    static async initWebGPULighting(): Promise<void> {
        if (FikoIsikYonsel.webGPUInitialized) return;

        if (FikoIsikYonsel.webGPUInitPromise) {
            return FikoIsikYonsel.webGPUInitPromise;
        }

        FikoIsikYonsel.webGPUInitPromise = (async () => {
            try {
                FikoIsikYonsel.webGPULighting = await FikoWebGPULighting.initialize();
                FikoIsikYonsel.webGPUInitialized = FikoIsikYonsel.webGPULighting.available;
            } catch (e) {
                FikoIsikYonsel.webGPUInitialized = false;
            }
        })();

        return FikoIsikYonsel.webGPUInitPromise;
    }

    /**
     * 🚀 Tüm headlight'ları GPU'da toplu güncelle
     * Birden fazla headlight varsa CPU'dan çok daha hızlı
     *
     * @param headlights Güncellenecek FikoIsikYonsel listesi
     * @param camera Aktif kamera
     */
    static async updateAllHeadlightsGPU(
        headlights: FikoIsikYonsel[],
        camera: THREE.Camera
    ): Promise<boolean> {
        // Filter active headlights
        const activeHeadlights = headlights.filter(h => h.visible && h.kameraTakipAktif);

        if (activeHeadlights.length === 0) return true;

        // CPU fallback for small counts or no WebGPU
        if (activeHeadlights.length < 4 || !FikoIsikYonsel.webGPULighting?.available) {
            // Use existing CPU method
            for (const light of activeHeadlights) {
                light.updateLightPosition();
            }
            return true;
        }

        // Prepare data for GPU
        const gpuData = activeHeadlights.map(light => ({
            position: light.position.clone(),
            offsetDistance: light.kameraOffsetUzakligi,
            manualOffset: light.kameraManuelOffset.clone(),
            isActive: light.kameraTakipAktif,
            targetPosition: light.target.position.clone(),
            intensity: light.intensity,
            color: light.color.clone()
        }));

        // GPU batch update
        const result = await FikoIsikYonsel.webGPULighting.updateHeadlightsBatch(gpuData, camera);

        if (!result) {
            // GPU failed, use CPU fallback
            for (const light of activeHeadlights) {
                light.updateLightPosition();
            }
            return false;
        }

        // Apply GPU results to lights
        for (let i = 0; i < activeHeadlights.length; i++) {
            const light = activeHeadlights[i];
            const posOff = i * 3;

            light.position.set(
                result.positions[posOff],
                result.positions[posOff + 1],
                result.positions[posOff + 2]
            );

            light.target.position.set(
                result.targetPositions[posOff],
                result.targetPositions[posOff + 1],
                result.targetPositions[posOff + 2]
            );

            light.target.updateMatrixWorld();
            light.updateMatrixWorld();
        }

        return true;
    }

    /**
     * 🚀 Light clustering hesapla (Forward+ rendering için)
     * 100+ ışık varken O(1) light lookup sağlar
     */
    static async computeLightClusters(
        lights: THREE.Light[],
        camera: THREE.Camera,
        screenSize: [number, number]
    ): Promise<any> {
        if (!FikoIsikYonsel.webGPULighting?.available) {
            return null;
        }

        // Convert Three.js lights to LightData format
        const lightData = lights
            .filter(l => l.visible)
            .map(light => {
                const isDirectional = light instanceof THREE.DirectionalLight;
                const isPoint = light instanceof THREE.PointLight;
                const isSpot = light instanceof THREE.SpotLight;

                return {
                    position: light.position.clone(),
                    direction: isDirectional
                        ? (light as THREE.DirectionalLight).target.position.clone().sub(light.position).normalize()
                        : new THREE.Vector3(0, -1, 0),
                    color: light.color.clone(),
                    intensity: light.intensity,
                    range: isPoint ? (light as THREE.PointLight).distance || 100 :
                           isSpot ? (light as THREE.SpotLight).distance || 100 : 10000,
                    type: isDirectional ? 'directional' as const :
                          isPoint ? 'point' as const :
                          isSpot ? 'spot' as const : 'directional' as const,
                    spotAngle: isSpot ? (light as THREE.SpotLight).angle : undefined,
                    spotPenumbra: isSpot ? (light as THREE.SpotLight).penumbra : undefined,
                    castShadow: light.castShadow,
                    visible: light.visible
                };
            });

        return FikoIsikYonsel.webGPULighting.computeLightClusters(lightData, camera, screenSize);
    }

    /**
     * WebGPU Lighting istatistiklerini al
     */
    static getWebGPUStats(): any {
        return FikoIsikYonsel.webGPULighting?.getStats() || null;
    }

    /**
     * WebGPU Lighting'in aktif olup olmadığını kontrol et
     */
    static get isWebGPUAvailable(): boolean {
        return FikoIsikYonsel.webGPULighting?.available || false;
    }
}